/**
 * How a place sits on the page at each size: a sheet with a right column on a computer and an iPad on its
 * side, a sheet that holds the right column's things on an iPad upright, a pushed screen with a round Back
 * on a phone. Also the small hooks every place shares.
 */
import { type HTMLAttributes, type ReactNode, type Ref, type RefObject, useEffect, useRef } from "react";
import { usePage } from "./context";
import { Pins } from "./Parts";
import { type Place, placeParams } from "./talk";

/** The class of a chat's body at this size. */
export const chatClass = (phone: boolean, posts = false) => `${phone ? "gq-chat" : "jp-chat"}${posts ? " posts" : ""}`;

/** The sheet's own element, for a place that keeps drop targets and measures: its ref, class and handlers. */
export type Sheet = HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> };

export function Frame({ head, nav, right, under, strip, sheet, children }: { head: ReactNode; nav: ReactNode; right?: ReactNode; under?: ReactNode; strip?: ReactNode; sheet?: Sheet; children: ReactNode }): ReactNode {
  const { lay } = usePage();
  if (lay === "phone")
    return (
      <div className="gq">
        {nav}
        {strip}
        {sheet ? <section {...sheet}>{children}</section> : children}
      </div>
    );
  return (
    <>
      <main className="jp-main">
        <section {...sheet} className={`jp-conv${sheet?.className ? ` ${sheet.className}` : ""}`}>
          {head}
          {lay === "upright" ? under : null}
          {lay === "upright" && right ? <div className="jp-inmid">{right}</div> : null}
          {children}
        </section>
      </main>
      {lay === "upright" ? null : <Pins>{right}</Pins>}
    </>
  );
}

/** Keep a chat scrolled to its newest line, unless the person has scrolled up to read. */
export function useStick(ref: RefObject<HTMLElement | null>, dep: unknown): void {
  const near = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      near.current = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [ref]);
  useEffect(() => {
    const el = ref.current;
    if (el && near.current) el.scrollTop = el.scrollHeight;
  }, [ref, dep]);
}

/** Mark a place seen once it has been read, and again whenever more arrives while it is open. */
export function useMarkSeen(place: Place, loaded: boolean, unseen: number, read?: unknown): void {
  const { seen } = usePage();
  const done = useRef("");
  const key = JSON.stringify(placeParams(place));
  useEffect(() => {
    if (!loaded) return;
    if (done.current !== key || unseen > 0) {
      done.current = key;
      seen(place);
    }
  }, [loaded, unseen, key, place, seen, read]);
}

/** The first value a place was read with, kept while it stays open: "New since you were here" must not move as it is read. */
export function useKept<T>(key: string, value: T | null | undefined): T | null {
  const kept = useRef<{ key: string; value: T | null }>({ key, value: null });
  if (kept.current.key !== key) kept.current = { key, value: null };
  if (kept.current.value === null && value !== undefined && value !== null) kept.current.value = value;
  return kept.current.value;
}
